import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart2, TrendingUp, TrendingDown, Download, Filter, Calendar,
  Truck, Package, Clock, DollarSign, MapPin, CheckCircle2
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell
} from 'recharts';

const LogisticsAnalytics = () => {
  const [timeRange, setTimeRange] = useState('6months');
  const [selectedVehicle, setSelectedVehicle] = useState('all');

  const deliveryPerformance = [
    { month: 'Jan', deliveries: 1250, onTime: 1185, delayed: 65, cost: 312500, efficiency: 94.8 },
    { month: 'Feb', deliveries: 1380, onTime: 1310, delayed: 70, cost: 345000, efficiency: 94.9 },
    { month: 'Mar', deliveries: 1520, onTime: 1450, delayed: 70, cost: 380000, efficiency: 95.4 },
    { month: 'Apr', deliveries: 1680, onTime: 1610, delayed: 70, cost: 420000, efficiency: 95.8 },
    { month: 'May', deliveries: 1820, onTime: 1750, delayed: 70, cost: 455000, efficiency: 96.2 },
    { month: 'Jun', deliveries: 1950, onTime: 1880, delayed: 70, cost: 487500, efficiency: 96.4 },
  ];

  const routeEfficiency = [
    { route: 'Riyadh → Jeddah', distance: 950, avgTime: 510, cost: 2800, efficiency: 96.2, trips: 45 },
    { route: 'Dammam → Riyadh', distance: 420, avgTime: 255, cost: 1800, efficiency: 94.8, trips: 38 },
    { route: 'Jeddah → Abha', distance: 680, avgTime: 405, cost: 2200, efficiency: 92.1, trips: 22 },
    { route: 'Riyadh → Tabuk', distance: 1100, avgTime: 620, cost: 3200, efficiency: 95.5, trips: 15 },
  ];

  const requestTypes = [
    { type: 'Warehouse → Store', count: 850, percentage: 45 },
    { type: 'Warehouse → Client', count: 620, percentage: 33 },
    { type: 'Warehouse → Warehouse', count: 280, percentage: 15 },
    { type: 'Internal Equipment', count: 150, percentage: 7 },
  ];

  const driverPerformance = [
    { driver: 'Ahmed Al-Rashid', deliveries: 245, onTimeRate: 96.5, avgRating: 4.8, efficiency: 98.2 },
    { driver: 'Mohammed Ali', deliveries: 189, onTimeRate: 94.2, avgRating: 4.6, efficiency: 96.5 },
    { driver: 'Omar Hassan', deliveries: 312, onTimeRate: 97.8, avgRating: 4.9, efficiency: 99.1 },
  ];

  const costAnalysis = [
    { category: 'Fuel', amount: 1250000, percentage: 45 },
    { category: 'Maintenance', amount: 450000, percentage: 16 },
    { category: 'Labor', amount: 800000, percentage: 29 },
    { category: 'Other', amount: 300000, percentage: 10 },
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  const handleExportPDF = () => {
    alert('Exporting logistics analytics report to PDF...');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Logistics Analytics</h2>
            <p className="text-sm text-gray-600 mt-1">Comprehensive performance metrics and insights</p>
          </div>
          <div className="flex gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="year">Last Year</option>
            </select>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Deliveries', value: '1950', trend: '+5.2%', icon: Package, color: 'blue' },
          { label: 'On-Time Rate', value: '96.4%', trend: '+0.6%', icon: CheckCircle2, color: 'green' },
          { label: 'Avg Cost per Delivery', value: 'SAR 250', trend: '-2.1%', icon: DollarSign, color: 'orange' },
          { label: 'Route Efficiency', value: '95.1%', trend: '+1.3%', icon: TrendingUp, color: 'purple' },
        ].map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 bg-${metric.color}-100 rounded-lg`}>
                  <Icon className={`w-5 h-5 text-${metric.color}-600`} />
                </div>
                <span className={`text-sm font-medium flex items-center gap-1 ${
                  metric.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  {metric.trend}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              <p className="text-sm text-gray-600 mt-1">{metric.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Performance Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={deliveryPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="deliveries" fill="#3b82f6" name="Total Deliveries" />
            <Line yAxisId="right" type="monotone" dataKey="efficiency" stroke="#10b981" strokeWidth={2} name="Efficiency %" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Route Efficiency Analysis</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={routeEfficiency}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="route" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="efficiency" fill="#3b82f6" name="Efficiency %" />
            <Bar dataKey="trips" fill="#10b981" name="Trips" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Types Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={requestTypes}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percentage }) => `${type}: ${percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {requestTypes.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costAnalysis}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percentage }) => `${category}: ${percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="amount"
              >
                {costAnalysis.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Driver Performance</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={driverPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="driver" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="deliveries" fill="#3b82f6" name="Total Deliveries" />
            <Bar dataKey="onTimeRate" fill="#10b981" name="On-Time Rate %" />
            <Bar dataKey="efficiency" fill="#f59e0b" name="Efficiency %" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LogisticsAnalytics;
